import { Link } from 'react-router-dom';
import type { UserRace, UserRaceStatus } from './home-types';

/**
 * Races the user sent us to build.
 *
 * Every row states where it is, because the alternative — a silent list — makes
 * someone who just uploaded tracks wonder whether it worked. Only a finished
 * race gets a watch link; the others say what happens next instead of dangling
 * a dead control.
 */

const STATUS: Record<UserRaceStatus, { label: string; cls: string; waiting: string }> = {
  ready: {
    label: 'Ready',
    cls: 'text-viewer-good bg-viewer-good/10 border-viewer-good/30',
    waiting: '',
  },
  building: {
    label: 'Being built',
    cls: 'text-viewer-amber bg-viewer-amber/10 border-viewer-amber/30',
    waiting: 'We’ll email you',
  },
  submitted: {
    label: 'Submitted',
    cls: 'text-viewer-muted bg-viewer-muted/10 border-viewer-muted/30',
    waiting: 'In the queue',
  },
};

function fmtDay(iso?: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function RaceRow({ race, compact = false }: { race: UserRace; compact?: boolean }) {
  const s = STATUS[race.status];
  const built = fmtDay(race.builtAt);
  const detail = [
    // Their OWN race number — first submission is Race 1, second is Race 2,
    // independent of any regatta (#1478) — so a run of similarly-named club
    // races is still tellable apart.
    race.userRaceNumber ? `Race ${race.userRaceNumber}` : null,
    race.whereWhen,
    !compact && race.trackCount ? `${race.trackCount} ${race.trackCount === 1 ? 'track' : 'tracks'}` : null,
    !compact && built ? `built ${built}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const body = (
    <>
      <span
        className={`shrink-0 rounded-md border px-2 py-0.5 font-viewer-mono text-[8.5px] uppercase tracking-wider ${s.cls}`}
      >
        {s.label}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] font-semibold text-viewer-ink">{race.name}</span>
        {detail && <span className="mt-0.5 block truncate text-[11.5px] text-viewer-muted">{detail}</span>}
      </span>
      {race.status === 'ready' ? (
        <span className="shrink-0 text-[12px] font-bold text-viewer-accent">Watch ▶</span>
      ) : (
        <span className="shrink-0 text-[10.5px] text-viewer-faint">{s.waiting}</span>
      )}
    </>
  );

  const shell =
    'mb-2 flex items-center gap-3 rounded-xl border border-viewer-line/25 bg-viewer-panel px-3.5 py-3 transition-colors';

  // Only a finished race is a link — the rest have nowhere to go yet.
  return race.status === 'ready' && race.raceId ? (
    <Link to={`/view/${race.raceId}`} className={`${shell} hover:border-viewer-line2/40`}>
      {body}
    </Link>
  ) : (
    <div className={shell}>{body}</div>
  );
}

export function YourRaces({
  races,
  limit,
  showHeader = true,
}: {
  races: UserRace[];
  /** Homepage shows a few; the My races page shows everything. */
  limit?: number;
  showHeader?: boolean;
}) {
  const shown = limit ? races.slice(0, limit) : races;

  return (
    <div>
      {showHeader && (
        <div className="mb-3 flex items-baseline gap-2.5">
          <h2 className="font-viewer-display text-[14px] font-bold uppercase tracking-[.13em] text-viewer-ink">
            Your races
          </h2>
          <span className="text-[11px] text-viewer-faint">{races.length}</span>
          {limit && races.length > limit && (
            <Link to="/my-races" className="ml-auto text-[11.5px] font-semibold text-viewer-accent hover:underline">
              See all
            </Link>
          )}
        </div>
      )}

      {shown.map((r) => (
        <RaceRow key={r.submissionId} race={r} compact={!!limit} />
      ))}
    </div>
  );
}

/** Shown in place of the list when they haven't sent us anything yet. */
export function NoRacesYet() {
  return (
    <div className="rounded-xl border border-dashed border-viewer-line2/35 px-4 py-5 text-center">
      <p className="text-[12.5px] text-viewer-muted">
        Raced somewhere we don’t cover yet? Send us the tracks and we’ll build it.
      </p>
      <Link
        to="/create-race"
        className="mt-2.5 inline-block text-[12.5px] font-semibold text-viewer-accent hover:underline"
      >
        Create your own race
      </Link>
    </div>
  );
}
